import { useState, useCallback, useEffect, useRef } from 'react';
import { PaginatedResponse, QueryParameters } from '@/services/mock/pagination';

interface UseInfiniteScrollOptions<T, P extends QueryParameters<T>> {
  fetchFn: (params: P) => Promise<PaginatedResponse<T>> | PaginatedResponse<T>;
  params?: Omit<P, 'page'>;
  pageSize?: number;
  enabled?: boolean;
}

export function useInfiniteScroll<T, P extends QueryParameters<T> = QueryParameters<T>>({
  fetchFn,
  params = {} as Omit<P, 'page'>,
  pageSize = 25,
  enabled = true,
}: UseInfiniteScrollOptions<T, P>) {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  const hasNextPageRef = useRef(hasNextPage);
  hasNextPageRef.current = hasNextPage;
  const pageRef = useRef(page);
  pageRef.current = page;

  // Stringify search/filter params for change detection
  const paramsKey = JSON.stringify(params);

  // Fetch a specific page and update accumulated list
  const loadPage = useCallback(
    async (targetPage: number, isReset = false) => {
      if (!enabled) return;

      if (isReset) {
        setIsLoading(true);
        setError(null);
      } else {
        if (isFetchingRef.current || !hasNextPageRef.current) return;
        setIsFetchingNextPage(true);
      }

      isFetchingRef.current = true;

      try {
        const queryParams = {
          ...params,
          page: targetPage,
          pageSize,
        } as unknown as P;

        const response = await Promise.resolve(fetchFnRef.current(queryParams));

        if (isReset) {
          setItems(response.data);
        } else {
          setItems((prev) => {
            // Deduplicate items by ID if available
            const existingIds = new Set(
              prev.map((item) => (item as Record<string, unknown>).id).filter(Boolean)
            );
            const newUniqueItems = response.data.filter(
              (item) => !existingIds.has((item as Record<string, unknown>).id)
            );
            return [...prev, ...newUniqueItems];
          });
        }

        setPage(response.page);
        setTotal(response.total);
        setTotalPages(response.totalPages);
        setHasNextPage(response.hasNextPage);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch additional records');
      } finally {
        setIsLoading(false);
        setIsFetchingNextPage(false);
        isFetchingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageSize, enabled, paramsKey]
  );

  // Initial load or reset when search/filter params change
  useEffect(() => {
    setPage(1);
    loadPage(1, true);
  }, [paramsKey, loadPage]);

  // Fetch next page trigger
  const fetchNextPage = useCallback(() => {
    if (hasNextPageRef.current && !isFetchingRef.current && !isLoading) {
      loadPage(pageRef.current + 1, false);
    }
  }, [isLoading, loadPage]);

  // Sentinel ref callback for IntersectionObserver
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading || isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      if (node && hasNextPageRef.current) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting && hasNextPageRef.current && !isFetchingRef.current) {
              fetchNextPage();
            }
          },
          { rootMargin: '200px', threshold: 0.1 }
        );
        observerRef.current.observe(node);
      }
    },
    [fetchNextPage, isFetchingNextPage, isLoading]
  );

  const retry = useCallback(() => {
    setError(null);
    loadPage(1, true);
  }, [loadPage]);

  const removeLocalItem = useCallback((id: string) => {
    setItems((prev) =>
      prev.filter((item) => {
        const record = item as Record<string, unknown>;
        return record.id !== id && record.requestNumber !== id;
      })
    );
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  return {
    items,
    page,
    total,
    totalPages,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
    sentinelRef,
    retry,
    removeLocalItem,
  };
}
